import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserService } from '../../services/user.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { CreateUserDTO, UserFullInfo } from 'src/app/user/model/user';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ApiError } from 'src/app/trip-calculator/models/api-response';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ToastModule } from 'primeng/toast';
import { SelectModule } from 'primeng/select';
import { Location } from '@angular/common';
import { CompanyService } from '../../services/company.service';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { BreadcrumbService } from '../../services/breadcrumb.service';

@Component({
  selector: 'app-edit-user',
  imports: [
    ButtonModule,
    InputTextModule,
    ReactiveFormsModule,
    ToastModule,
    SelectModule,
    ConfirmDialogModule,
    TooltipModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './edit-user.component.html',
})
export class EditUserComponent implements OnInit {
  fb = inject(FormBuilder);
  userService = inject(UserService);
  companyService = inject(CompanyService);
  activeRoute = inject(ActivatedRoute);
  router = inject(Router);
  confirmationService = inject(ConfirmationService);
  breadcrumbService = inject(BreadcrumbService);
  selectCompanyOptions: { label: string; value: string }[] = [];

  http = inject(HttpClient);
  notificationService = inject(NotificationService);
  location = inject(Location);
  user?: UserFullInfo;
  editMode = false;
  isResettingPassword = false;
  isDeleting = false;
  userForm = this.fb.group({
    firstName: [''],
    lastName: [''],
    email: ['', Validators.email],
    phone: [''],
    role: [''],
    companyId: ['', Validators.required],
  });

  ngOnInit() {
    this.setMode();
    this.initializeForm();
    this.getCompanyList();
  }

  setMode() {
    const id = this.activeRoute.snapshot.params['id'];
    if (id) {
      this.editMode = true;
    }
  }

  initializeForm() {
    const id = this.activeRoute.snapshot.params['id'];
    if (id) {
      this.userService.getUser(id).subscribe({
        next: (user) => {
          this.user = user;
          this.userForm.patchValue({
            firstName: user.firstName,
            lastName: user.lastName,
            companyId: user.companyId,
            email: user.emails?.[0]?.email,
            phone: user.phone,
            role: user.role,
          });

          // Set breadcrumb label
          if (user.id) {
            const displayName =
              user.firstName && user.lastName
                ? `${user.firstName} ${user.lastName}`
                : user.emails?.[0]?.email || user.id;
            this.breadcrumbService.setLabel(user.id, displayName);
          }
        },
        error: (err) => console.error('Error fetching user:', err),
      });
    }
  }

  getCompanyList() {
    this.companyService.getCompanyList().subscribe({
      next: (companies) => {
        this.selectCompanyOptions = companies.map((company) => ({
          label: company.name ?? '--',
          value: company.id!,
        }));
      },
      error: (error) => {
        console.error('Error loading companies:', error);
      },
    });
  }

  submitForm() {
    if (this.userForm.valid) {
      const formValues = this.userForm.value;
      const userDto: CreateUserDTO = {
        ...this.user,
        companyId: formValues.companyId ?? '',
        firstName: formValues.firstName ?? '',
        lastName: formValues.lastName ?? '',
        email: formValues.email ?? '',
        phone: formValues.phone ?? '',
        role: formValues.role ?? '',
      };
      const id = this.activeRoute.snapshot.params['id'];
      if (id) {
        this.userService.update(id, userDto).subscribe({
          next: () => {
            this.navigateBack();
          },
          error: (err: ApiError) => {
            this.notificationService.error(err.error.message);
          },
        });
      } else {
        this.userService.create(userDto).subscribe({
          next: () => {
            this.navigateBack(); // Navigate to user list after creation
          },
          error: (err: ApiError) => {
            this.notificationService.error(err.error.message);
          },
        });
      }
    } else {
      this.notificationService.error('Form is invalid');
    }
  }
  navigateBack() {
    this.location.back();
  }

  triggerPasswordReset() {
    if (!this.user?.id) return;

    const userEmail = this.user.emails?.[0]?.email || 'this user';
    this.confirmationService.confirm({
      message: `Send a password reset email to ${userEmail}?`,
      header: 'Confirm Password Reset',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Send Email',
      rejectLabel: 'Cancel',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this.isResettingPassword = true;
        this.userService.triggerPasswordReset(this.user!.id!).subscribe({
          next: () => {
            this.isResettingPassword = false;
            this.notificationService.success(
              `Password reset email sent to ${userEmail}`,
            );
          },
          error: (err: ApiError) => {
            this.isResettingPassword = false;
            this.notificationService.error(
              err.error.message || 'Failed to send password reset email',
            );
          },
        });
      },
    });
  }

  confirmDelete() {
    if (!this.user?.id) return;

    const userName =
      this.user.firstName && this.user.lastName
        ? `${this.user.firstName} ${this.user.lastName}`
        : this.user.emails?.[0]?.email || 'this user';

    this.confirmationService.confirm({
      message: `Are you sure you want to delete ${userName}? This action cannot be undone.`,
      header: 'Delete User',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this.deleteUser();
      },
    });
  }

  deleteUser() {
    if (!this.user?.id) return;

    this.isDeleting = true;
    this.userService.delete(this.user.id).subscribe({
      next: () => {
        this.isDeleting = false;
        this.notificationService.success('User deleted successfully');
        this.router.navigate(['/admin/user']);
      },
      error: (err: ApiError) => {
        this.isDeleting = false;
        this.notificationService.error(
          err.error?.message || 'Failed to delete user',
        );
      },
    });
  }
}
